/**
 * Where measure bars draw on screen, shared by the room editor and the
 * assembler: which side of the measured region each bar family rides, a
 * bar's projection to screen, and keeping a family of bars reachable on the
 * stage however far the view is zoomed in.
 */

import { expandPlanBounds, type PlanBounds, type Point, WALL_THICKNESS, worldOnLine } from '@kaiizen/kai-floor-plan';
import type { MeasureBar } from '../editorState';
import { type InputKind, MIN_TOUCH_TARGET_PX } from '../inputKind';
import { formatFeetInches } from '../lengthFormat';
import { modelToScreen, type Viewport } from '../viewport';

/** Where a measure bar draws on screen: its two endpoints and its label's center. */
export interface BarScreen {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  cx: number;
  cy: number;
}

/** Which side of the geometry each bar family sits on. */
export interface DimBarSides {
  horizontal: 'top' | 'bottom';
  vertical: 'left' | 'right';
}

/** A measure bar with its screen projection. */
export interface PlacedBar {
  bar: MeasureBar;
  g: BarScreen;
}

/** About how wide a bar's chip renders (px): its widest form, feet and inches, in the chips' mono font. */
const chipWidth = (bar: MeasureBar): number => formatFeetInches(bar.value).length * 7 + 16;

/** Half a chip's height plus a little air (px): how close to the stage's top or bottom a chip may sit. */
const CHIP_MARGIN_Y = 12;
/** The widest chip the editors expect, `99' 11½"` in the chips' mono font (px). */
const CHIP_MAX_WIDTH = 72;
/** Half the widest chip plus a little air (px): how close to the stage's left or right a chip may sit. */
const CHIP_MARGIN_X = CHIP_MAX_WIDTH / 2 + 6;
/** Half a bar's end tick plus a little air (px). */
const TICK_MARGIN = 7;
/** How far outside the drawn wall band the bars ride (px). */
const BAR_OFFSET = 30;

/**
 * How far a crowded chip moves off its bar's middle (px): a horizontal bar's
 * chip staggers outward, a vertical bar's shifts aside past its own half
 * width. Under touch each move is at least a touch target, so the chip's
 * target clears its neighbors'.
 */
const crowdedChipOffset = (inputKind: InputKind, labelWidth: number): { stagger: number; shift: number } =>
  inputKind === 'touch'
    ? { stagger: MIN_TOUCH_TARGET_PX, shift: Math.max(MIN_TOUCH_TARGET_PX, labelWidth / 2 + 8) }
    : { stagger: 15, shift: labelWidth / 2 + 8 };

/**
 * The sides of `region` nearest `anchor`, the center of what's measured, so
 * the measurements sit next to what's being edited. Top and left without an
 * anchor or a region.
 */
export function dimBarSidesFor(anchor: Point | null, region: PlanBounds | null): DimBarSides {
  if (!anchor || !region) return { horizontal: 'top', vertical: 'left' };
  return {
    horizontal: anchor.y > (region.minY + region.maxY) / 2 ? 'bottom' : 'top',
    vertical: anchor.x > (region.minX + region.maxX) / 2 ? 'right' : 'left',
  };
}

/** The one shift that brings a unit of bars, chips and ticks together, onto the stage's held axis; the near edge wins. */
function unitShift(unit: readonly PlacedBar[], horizontal: boolean, size: number): number {
  if (!unit.length) return 0;
  const chipHalf = horizontal ? CHIP_MARGIN_Y : CHIP_MARGIN_X;
  let low = Infinity, high = -Infinity;
  for (const { g } of unit) {
    const chip = horizontal ? g.cy : g.cx, line = horizontal ? g.ay : g.ax;
    low = Math.min(low, chip - chipHalf, line - TICK_MARGIN);
    high = Math.max(high, chip + chipHalf, line + TICK_MARGIN);
  }
  return low < 0 ? -low : high > size ? size - high : 0;
}

/**
 * Keep the measure bars reachable however far the view is zoomed in. The
 * horizontal bars (measuring x, riding above or below the geometry) move as
 * one unit, held within the stage's top and bottom edges and free to leave
 * left or right; the vertical bars (measuring y, beside the geometry) move
 * as another, held within the left and right edges and free to leave top or
 * bottom. A unit shifts by one amount, lines, end ticks, and chips together,
 * so a family of three (gap, size, gap) keeps its alignment. Diagonal bars
 * ride their line. The room kept at the sides is fixed, sized for the widest
 * chip the editors expect.
 */
export function barsOnStage(placed: readonly PlacedBar[], stageW: number, stageH: number): PlacedBar[] {
  const shifts = {
    h: unitShift(placed.filter(({ bar }) => bar.type === 'h'), true, stageH),
    v: unitShift(placed.filter(({ bar }) => bar.type === 'v'), false, stageW),
  };
  return placed.map(({ bar, g }) => {
    const shift = bar.type === 'd' ? 0 : shifts[bar.type];
    if (!shift) return { bar, g };
    return bar.type === 'h'
      ? { bar, g: { ...g, ay: g.ay + shift, by: g.by + shift, cy: g.cy + shift } }
      : { bar, g: { ...g, ax: g.ax + shift, bx: g.bx + shift, cx: g.cx + shift } };
  });
}

/**
 * Project a measure bar to screen. Bars sit outside the drawn wall band, one
 * wall thickness past the measured `region`: horizontal bars ride above or
 * below it and vertical bars left or right of it (per `sides`), diagonal bars
 * sit just off their line frame. A crowded bar's label nudges outward, away
 * from the geometry, by an offset the input sets.
 */
export function measureBarScreen(
  viewport: Viewport,
  region: PlanBounds,
  { horizontal, vertical }: DimBarSides,
  bar: MeasureBar,
  inputKind: InputKind = 'pointer',
): BarScreen {
  const bounds = expandPlanBounds(region, WALL_THICKNESS);
  let ax: number, ay: number, bx: number, by: number;
  if (bar.type === 'h') {
    const y = horizontal === 'bottom'
      ? modelToScreen(viewport, { x: 0, y: bounds.maxY }).y + BAR_OFFSET
      : modelToScreen(viewport, { x: 0, y: bounds.minY }).y - BAR_OFFSET;
    ax = modelToScreen(viewport, { x: bar.from, y: 0 }).x;
    bx = modelToScreen(viewport, { x: bar.to, y: 0 }).x;
    ay = by = y;
  } else if (bar.type === 'v') {
    const x = vertical === 'right'
      ? modelToScreen(viewport, { x: bounds.maxX, y: 0 }).x + BAR_OFFSET
      : modelToScreen(viewport, { x: bounds.minX, y: 0 }).x - BAR_OFFSET;
    ay = modelToScreen(viewport, { x: 0, y: bar.from }).y;
    by = modelToScreen(viewport, { x: 0, y: bar.to }).y;
    ax = bx = x;
  } else {
    const lineFrame = bar.lf!;
    const perpModel = BAR_OFFSET / viewport.scale;
    const screenA = modelToScreen(viewport, worldOnLine(lineFrame, bar.from, perpModel));
    const screenB = modelToScreen(viewport, worldOnLine(lineFrame, bar.to, perpModel));
    ax = screenA.x;
    ay = screenA.y;
    bx = screenB.x;
    by = screenB.y;
  }
  const labelWidth = chipWidth(bar);
  const { stagger, shift } = crowdedChipOffset(inputKind, labelWidth);
  let cx = (ax + bx) / 2, cy = (ay + by) / 2;
  if (bar.type === 'h') {
    if (Math.abs(bx - ax) < labelWidth + 6) cy += horizontal === 'bottom' ? stagger : -stagger;
  } else if (bar.type === 'v') {
    if (Math.abs(by - ay) < 22) cx += (vertical === 'right' ? 1 : -1) * shift;
  }
  return { ax, ay, bx, by, cx, cy };
}
